/**
 * The rowboat provider + sync loop + anon-claim wiring for RosaHouse.
 *
 * Provides the app's data + auth context. `@jbroll/rowboat-react`'s
 * `createRowboat(schema)` factory (used by an earlier version of `src/schema/folder.ts`)
 * deliberately keeps its `RowboatDb` instance private — it's an app-scoped singleton the
 * factory builds and never exposes (see that package's `factory.ts`: "App-scoped singletons
 * ... are created ONCE ... deliberately NEVER torn down"). But `syncWithServer` needs that
 * exact `RowboatDb` (pushing/pulling through a *different* connection to the same IndexedDB
 * database would write real data, but wouldn't notify the graph's own `ChangeEmitter` — the
 * UI would silently go stale after every sync). So this module builds the db itself (via the
 * lower-level `buildRowboatDb`/`storeName` from `@jbroll/rowboat-client`) and binds the graph
 * with the lower-level `useRowboat(schema, db)` hook, keeping both the graph and the db in one
 * place so the sync loop and the UI share the identical `RowboatDb` instance.
 *
 * `key={identity}` on `RowboatBridge` forces a full remount (fresh db + graph) when the
 * effective identity flips from `ANON_IDENTITY` to a real author id — the same "swap the
 * whole subtree" pattern `RowboatConfig`'s per-identity `storeName` implies. Any other change of
 * identity reloads the page (`useReloadOnAccountChange`).
 */

import { useAnonClaim, useSession } from '@jbroll/rowboat-auth-betterauth-react';
import {
  ANON_IDENTITY,
  buildRowboatDb,
  type RowboatDb,
  storeName,
  syncWithServer,
} from '@jbroll/rowboat-client';
import { useRowboat as useRawRowboat, useSelect as useRawSelect } from '@jbroll/rowboat-react';
import { compileSchema, type RelationalGraph } from '@jbroll/rowboat-schema';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ClaimBlockedBanner } from '@/components/ClaimBlockedBanner';
import { holdAnonStore, releaseAnonStore } from '@/lib/anonStoreHold';
import { useAuthor, useReloadOnAccountChange } from '@/lib/identity';
import { MIGRATIONS } from '@/lib/schemaMigrations';
import { serialSync } from '@/lib/serialSync';
import { onSyncGate } from '@/lib/serviceWorkerUpdate';
import { APP_STORE, APP_VERSION } from '@/lib/storeNames';
import { notifySynced } from '@/lib/syncSignal';
import { withSyncToken } from '@/lib/syncToken';
import { schema } from '@/schema/estate';

const SYNC_INTERVAL_MS = 5000;

// `<rowboatUrl>/db/<databaseId>/api/sync` — written by provision:* (see scripts/dev-rowboat.sh in
// dev, the deploy env in prod). syncWithServer appends /sync and /pull; the group mint is /groups.
const SYNC_BASE = import.meta.env.VITE_ROWBOAT_SYNC_BASE;
if (!SYNC_BASE) {
  throw new Error('VITE_ROWBOAT_SYNC_BASE is required — run `npm run provision:local` for dev');
}

const manifest = compileSchema(schema).manifest;

const dbOptions = { indexedDB: window.indexedDB, IDBKeyRange: window.IDBKeyRange };

const authorizedFetch = withSyncToken((input, init) => fetch(input, init));

interface PortContextValue {
  graph: RelationalGraph<typeof schema>;
  /** better-auth user id, or `null` when anonymous / not yet resolved. */
  author: string | null;
  /**
   * Mints a fresh owner_group_id for a new estate-scoped row — hosted rowboat's
   * `POST <SYNC_BASE>/groups`. Injected here (rather than hardcoded into a
   * service hook) because minting talks to the server, which is an app-layer
   * concern, not something a headless data hook should know about. `requestKey` makes a repeated
   * mint return the group the first one made: a one-off mint passes a fresh UUID, a mint that may
   * be repeated passes a key stable across the repeats.
   */
  mintGroup: (requestKey: string, parentGroupId?: string) => Promise<string>;
  /**
   * The scope group of a row in this identity's IndexedDB store, read from the store because
   * the graph's snapshot loads after mount and lags writes. Throws when the store has no such row.
   */
  storedGroup: (table: string, id: string) => Promise<string>;
}

const PortContext = createContext<PortContextValue | null>(null);

/** Runs inside the serial sync runner after each successful round, so it never overlaps a pull. */
type AfterSync = (graph: RelationalGraph<typeof schema>) => Promise<unknown>;

// Authenticated mint: hosted rowboat creates a scope group the caller admins, nested under their
// root group. Anonymous users have no token and never sync, so an anon estate gets a purely-local
// group id — adopt on sign-in moves its rows to a server group minted here.
async function serverMintGroup(requestKey: string, parentGroupId?: string): Promise<string> {
  const res = await authorizedFetch(`${SYNC_BASE}/groups`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ parentGroup: parentGroupId, requestKey }),
  });
  // NO FALLBACKS: a non-ok response (401 on an expired token, 403, 5xx) must surface, not
  // silently yield `undefined` and let an estate row be created with no scope group.
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`mintGroup: POST ${SYNC_BASE}/groups failed (${res.status}) ${body}`);
  }
  const { groupId } = (await res.json()) as { groupId?: unknown };
  if (typeof groupId !== 'string' || groupId.length === 0) {
    throw new Error(`mintGroup: response missing a groupId (${JSON.stringify(groupId)})`);
  }
  return groupId;
}

interface BridgeProps {
  identity: string;
  author: string | null;
  sessionPending: boolean;
  claiming: boolean;
  afterSync?: AfterSync;
  children: ReactNode;
}

const openStore = (name: string) =>
  buildRowboatDb(name, manifest, MIGRATIONS, undefined, dbOptions);

// One RowboatDb per identity, kept for the life of this (keyed) subtree. The anonymous store opens
// only once its hold is granted (`anonStoreHold`) and renders nothing until then.
function RowboatBridge(props: BridgeProps) {
  const name = storeName(APP_STORE, props.identity);
  const anon = props.identity === ANON_IDENTITY;
  const [db, setDb] = useState<RowboatDb | null>(() => (anon ? null : openStore(name)));
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  if (failure) throw failure.error;

  useEffect(() => {
    if (db !== null) return;
    let live = true;
    holdAnonStore(name).then(
      () => {
        if (live) setDb(openStore(name));
      },
      (error: unknown) => {
        if (live) setFailure({ error });
      },
    );
    return () => {
      live = false;
    };
  }, [db, name]);

  // Debounced close: React StrictMode dev-mode double-invokes effects (mount -> cleanup ->
  // mount, synchronously, to surface unsafe cleanup) to simulate a remount. A synchronous
  // `db.close()` here would permanently close this singleton on the very first render — the
  // component function itself isn't re-invoked by that simulation, so nothing ever reopens it,
  // and every subsequent read/write (including `syncWithServer`) fails with a Dexie
  // `DatabaseClosedError` for the rest of the page's life. Deferring the close and letting the
  // immediate StrictMode remount cancel it (same pattern as any StrictMode-fragile resource
  // teardown) makes this correct in dev while still closing normally on a genuine unmount. The
  // anonymous store's hold goes with the close, which is what lets a sign-in claim take it.
  const pendingCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (pendingCloseRef.current !== null) {
      clearTimeout(pendingCloseRef.current);
      pendingCloseRef.current = null;
    }
    return () => {
      pendingCloseRef.current = setTimeout(() => {
        db?.close();
        if (anon) releaseAnonStore(name);
        pendingCloseRef.current = null;
      }, 0);
    };
  }, [db, anon, name]);

  if (db === null) return null;
  return <StoreBridge {...props} db={db} />;
}

function StoreBridge({
  identity,
  author,
  sessionPending,
  claiming,
  afterSync,
  children,
  db,
}: BridgeProps & { db: RowboatDb }) {
  const graph = useRawRowboat(schema, db);
  const afterSyncRef = useRef(afterSync);
  afterSyncRef.current = afterSync;

  // One sync at a time per db. Overlapping rounds pull each other's pushed rows as foreign, and
  // that whole-row write clobbers any local edit made in between; the edit's own later push is a
  // block the client skips on pull, so the local row never recovers.
  const sync = useMemo(
    () =>
      author
        ? serialSync(async () => {
            await syncWithServer({
              db,
              apiBase: SYNC_BASE,
              appVersion: APP_VERSION,
              author,
              migration: { onGate: onSyncGate },
              fetchFn: authorizedFetch,
            });
            notifySynced();
            await afterSyncRef.current?.(graph);
          })
        : null,
    [db, graph, author],
  );

  // Drive push/pull on an interval while signed in. Anonymous users never sync — there is no
  // server-side scope group for the anon identity, so a sync attempt would have nothing valid
  // to push against.
  useEffect(() => {
    if (!sync) return;
    let cancelled = false;
    const run = async () => {
      try {
        await sync();
      } catch (err) {
        console.error('[rowboat] syncWithServer failed:', err);
      }
    };
    void run();
    const id = window.setInterval(() => {
      if (!cancelled) void run();
    }, SYNC_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sync]);

  // Anonymous users can't hit the auth-gated mint route — give them a local group id (adopt
  // re-scopes on sign-in). Signed-in users mint a server-side group they admin.
  const mintGroup = useMemo<PortContextValue['mintGroup']>(
    () => (author ? serverMintGroup : async () => crypto.randomUUID()),
    [author],
  );

  // Provision per-user starter data once this identity's store has hydrated. The check is
  // authoritative — it reads the persisted store DIRECTLY (`db.table`), because the in-memory
  // graph snapshot may not have loaded yet and a premature empty-read would create a duplicate.
  //   (1) The `user_settings` singleton (write paths hard-error without it). Deterministic
  //       id+group = the identity: for a signed-in user that's `user.id`, whose root scope group
  //       is also `user.id`, so the row converges across devices with no minted group. Anonymous
  //       users get a local, never-synced ANON_IDENTITY-scoped row.
  //   (2) Nothing else: no seed data, no cleanup, no subscription sync. Capture UI
  //       creates estates/item groups/categories explicitly.
  const provisionedRef = useRef(false);
  useEffect(() => {
    // Wait until the auth session has SETTLED before provisioning. On a reload of a signed-in
    // user with no remembered author, `useAuthor()` is briefly null (session still resolving), so
    // this bridge first mounts on the ANON identity; provisioning here would write into the transient
    // anon store, which `useAnonClaim` then adopts into the account — a duplicate row on every
    // reload. Gating on `!sessionPending` means the anon phase never provisions; once the session
    // settles we either provision a genuine anonymous user or, for a signed-in user, run
    // against the authenticated store (which already has its data, so nothing is re-created).
    if (provisionedRef.current || sessionPending || claiming) return;
    let cancelled = false;
    void (async () => {
      try {
        // For a signed-in user, PULL the server's state before deciding. On a reload the local
        // store is cold — the account's data lives on the server and arrives via an async pull —
        // so an empty-check here would (wrongly) read "new user" and provision a duplicate row
        // on every reload. Awaiting one sync populates the local store from the server
        // first, making the checks below reflect the account's real data. Anonymous users have no
        // server, so their local store is already authoritative — skip the pull for them.
        if (sync) await sync();
        if (cancelled || provisionedRef.current) return;

        const existingSettings = await db
          .table('user_settings')
          .filter((r) => !r.__deleted)
          .toArray();
        if (cancelled || provisionedRef.current) return;
        if (existingSettings.length === 0) {
          await graph.user_settings.create({
            id: identity,
            owner_group_id: identity,
            current_estate_id: '',
            current_item_group_id: '',
            current_category_id: '',
          });
        }
        provisionedRef.current = true;
      } catch (err) {
        console.error('[rowboat] account-init provisioning failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db, graph, identity, sessionPending, sync, claiming]);

  const storedGroup = useMemo<PortContextValue['storedGroup']>(
    () => async (table, id) => {
      const column = db.scopeCol(table);
      if (!column) throw new Error(`storedGroup: ${table} has no scope column`);
      const group = (await db.table(table).get(id))?.[column];
      if (typeof group !== 'string' || group === '')
        throw new Error(`storedGroup: this store has no ${table} row ${id} with a group`);
      return group;
    },
    [db],
  );

  const value = useMemo<PortContextValue>(
    () => ({ graph, author, mintGroup, storedGroup }),
    [graph, author, mintGroup, storedGroup],
  );

  return <PortContext.Provider value={value}>{children}</PortContext.Provider>;
}

export function RowboatProvider({
  afterSync,
  children,
}: {
  afterSync?: AfterSync;
  children: ReactNode;
}) {
  const author = useAuthor();
  const { isPending: sessionPending } = useSession();
  const identity = author ?? ANON_IDENTITY;

  const switched = useReloadOnAccountChange(author);

  // Claims the anon store into the authenticated identity's store on login. The `key` below
  // remounts `RowboatBridge` (fresh db + graph) once `author` flips, so the claimed rows show
  // up without any extra wiring here. `mintScope` gives each anonymous project its own server
  // group, so sharing one never hands over the account's root group. The claim's key is stable
  // across a lost reply and a resume, so each project gets one group however often it is sent.
  const { claiming, blockedByOtherTab, retry } = useAnonClaim({
    app: APP_STORE,
    tables: manifest,
    migrations: MIGRATIONS,
    options: dbOptions,
    mintScope: (_anonScope, requestKey) => serverMintGroup(requestKey),
    onError: (err) => {
      console.error('[rowboat] useAnonClaim failed:', err);
    },
  });

  if (switched) return null;
  return (
    <>
      {blockedByOtherTab !== null && <ClaimBlockedBanner onRetry={retry} />}
      <RowboatBridge
        key={identity}
        identity={identity}
        author={author}
        sessionPending={sessionPending}
        claiming={claiming}
        afterSync={afterSync}
      >
        {children}
      </RowboatBridge>
    </>
  );
}

/** The bound graph — must be used inside `<RowboatProvider>`. */
export function useRowboat(): RelationalGraph<typeof schema> {
  const ctx = useContext(PortContext);
  if (!ctx) throw new Error('useRowboat() must be used inside <RowboatProvider>');
  return ctx.graph;
}

/**
 * Reactive slice of the graph — same signature as the old `createRowboat`-bound `useSelect`.
 *
 * WARNING: selectors returning objects or arrays MUST pass a shape-aware `isEqual` — rowboat
 * rows are rebuilt on every read, so a fresh object/array per call never settles under the
 * default Object.is ("Maximum update depth exceeded"). Compare length/ids/rendered fields only,
 * like the `sameRows`/`sameContext` comparators in `src/hooks/selectEqual.ts`.
 */
export function useSelect<T>(selector: () => T, isEqual?: (a: T, b: T) => boolean): T {
  return useRawSelect(useRowboat(), selector, isEqual);
}

/** The port-specific bits the estate service hooks need that the graph itself doesn't carry. */
export function usePort(): Omit<PortContextValue, 'graph'> {
  const ctx = useContext(PortContext);
  if (!ctx) throw new Error('usePort() must be used inside <RowboatProvider>');
  return { author: ctx.author, mintGroup: ctx.mintGroup, storedGroup: ctx.storedGroup };
}
